import React from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // In production, you might want to send this to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Prevent infinite error loops
      if (this.state.errorCount > 5) {
        return (
          <div className="error-boundary">
            <div className="error-boundary-container">
              <div className="error-boundary-icon critical">
                <AlertTriangle size={60} />
              </div>
              <h1>Critical Error</h1>
              <p>The application has encountered multiple errors. Please refresh the page or contact support.</p>
              <div className="error-boundary-actions">
                <button onClick={this.handleRefresh} className="error-btn primary">
                  <RefreshCw size={18} />
                  <span>Refresh Page</span>
                </button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-container">
            <div className="error-boundary-icon">
              <AlertTriangle size={60} />
            </div>

            <h1 className="error-boundary-title">Something went wrong</h1>
            <p className="error-boundary-message">
              We're sorry for the inconvenience. The application has encountered an unexpected error.
            </p>

            <div className="error-boundary-actions">
              <button onClick={this.handleReset} className="error-btn primary">
                Try Again
              </button>
              <button onClick={this.handleRefresh} className="error-btn secondary">
                <RefreshCw size={18} />
                <span>Refresh Page</span>
              </button>
              <button onClick={this.handleGoHome} className="error-btn secondary">
                <Home size={18} />
                <span>Go Home</span>
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-boundary-stack">
                  <h3>Error Message:</h3>
                  <pre>{this.state.error.toString()}</pre>

                  {this.state.errorInfo && (
                    <>
                      <h3>Component Stack:</h3>
                      <pre>{this.state.errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}

            <div className="error-boundary-help">
              <p>If this problem persists, please <a href="mailto:support@tech360.com">contact support</a></p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
